<script>
  import Icon from "@iconify/svelte";
  import { onMount } from "svelte";
  import { t } from "../lib/i18n.svelte.js";
  import { api } from "../lib/api.svelte.js";
  import { addToast } from "../components/Toast.svelte";
  import Layout from "../components/Layout.svelte";
  import SlideOver from "../components/SlideOver.svelte";
  import Skeleton from "../components/Skeleton.svelte";
  import EmptyState from "../components/EmptyState.svelte";

  let viewers = $state([]);
  let total = $state(0);
  let initialLoad = $state(true);
  let error = $state("");

  let search = $state("");
  let page = $state(1);
  let perPage = $state(15);
  let sortField = $state("label");
  let sortOrder = $state("asc");

  let totalPages = $derived(Math.max(1, Math.ceil(total / perPage)));

  let pageNumbers = $derived.by(() => {
    const tp = totalPages;
    const cur = page;
    if (tp <= 7) return Array.from({ length: tp }, (_, i) => i + 1);
    const pages = [];
    pages.push(1);
    if (cur > 3) pages.push("...");
    for (let i = Math.max(2, cur - 1); i <= Math.min(tp - 1, cur + 1); i++) {
      pages.push(i);
    }
    if (cur < tp - 2) pages.push("...");
    pages.push(tp);
    return pages;
  });

  // Drawer state
  let drawerItem = $state(null);
  let editLabel = $state("");
  let editStatus = $state("active");
  let editAllSites = $state(false);
  let saving = $state(false);
  let deleteConfirm = $state(false);
  let deleting = $state(false);

  function toggleSort(field) {
    if (sortField === field) {
      sortOrder = sortOrder === "asc" ? "desc" : "asc";
    } else {
      sortField = field;
      sortOrder = "asc";
    }
    page = 1;
    fetchData();
  }

  async function fetchData() {
    try {
      const q = encodeURIComponent(search);
      const res = await api.get(`/viewers?page=${page}&per_page=${perPage}&q=${q}&sort=${sortField}&order=${sortOrder}`);
      viewers = res.data || [];
      total = res.total || 0;
    } catch (e) {
      if (initialLoad) error = e.message || t("viewers.failed_load");
    }
    initialLoad = false;
  }

  function handleSearch() {
    page = 1;
    fetchData();
  }

  function goPage(p) {
    page = p;
    fetchData();
  }

  function handlePerPage(val) {
    perPage = Number(val);
    page = 1;
    fetchData();
  }

  function openDrawer(viewer) {
    drawerItem = viewer;
    editLabel = viewer.label || "";
    editStatus = viewer.status || "active";
    editAllSites = !!viewer.all_sites;
    deleteConfirm = false;
  }

  function closeDrawer() {
    drawerItem = null;
    deleteConfirm = false;
  }

  async function handleSave() {
    if (!drawerItem) return;
    saving = true;
    try {
      await api.put(`/viewers/${drawerItem.id}`, {
        label: editLabel,
        status: editStatus,
        all_sites: editAllSites,
      });
      addToast(t("viewers.updated"), "success");
      await fetchData();
      const updated = viewers.find((v) => v.id === drawerItem.id);
      if (updated) openDrawer(updated);
    } catch (e) {
      addToast(e.message || t("viewers.failed_save"), "error");
    }
    saving = false;
  }

  async function confirmDelete() {
    if (!drawerItem) return;
    deleting = true;
    try {
      await api.del(`/viewers/${drawerItem.id}`);
      addToast(t("viewers.deleted"), "success");
      closeDrawer();
      await fetchData();
    } catch (e) {
      addToast(e.message || t("viewers.failed_delete"), "error");
    }
    deleting = false;
  }

  function formatDate(d) {
    if (!d) return "-";
    return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  }

  onMount(() => {
    fetchData();
    const id = setInterval(() => {
      if (document.visibilityState !== "hidden") fetchData();
    }, 10000);
    return () => clearInterval(id);
  });
</script>

<Layout>
  <div class="flex gap-4">
    <div class="flex-1 min-w-0 {drawerItem ? 'lg:mr-[480px]' : ''}">
      <div class="ui-card p-5 sm:p-6">
        <!-- Header -->
        <div class="flex items-start gap-3 mb-6">
          <span class="ui-header-icon">
            <Icon icon="mdi:account-group" class="w-[18px] h-[18px]" />
          </span>
          <div class="flex-1">
            <h1 class="text-lg font-normal tracking-wide text-zinc-100">{t("viewers.title")}</h1>
            <p class="mt-0.5 text-sm text-zinc-400">{t("viewers.subtitle")}</p>
          </div>
        </div>

        <!-- Control bar -->
        <div class="flex items-center gap-3 flex-wrap mb-4">
          <!-- Search -->
          <div class="flex-1 min-w-[180px]">
            <input
              type="text"
              class="w-full h-9 px-3 bg-black/20 border border-white/10 rounded text-sm text-zinc-200 placeholder-zinc-500 focus:outline-none focus:border-white/25"
              placeholder={t("common.search_placeholder")}
              bind:value={search}
              oninput={handleSearch}
            />
          </div>

          <!-- Per page -->
          <select
            class="h-9 px-2 bg-black/20 border border-white/10 rounded text-sm text-zinc-300 focus:outline-none focus:border-white/25 cursor-pointer"
            value={perPage}
            onchange={(e) => handlePerPage(e.target.value)}
          >
            <option value={15}>15</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>

          <!-- Refresh -->
          <button
            class="h-9 w-9 flex items-center justify-center bg-black/20 border border-white/10 rounded text-zinc-400 hover:text-white hover:border-white/25 transition-colors"
            onclick={fetchData}
            title={t("common.refresh")}
          >
            <Icon icon="mdi:refresh" class="w-4 h-4" />
          </button>

          <!-- Pagination -->
          <div class="flex items-center gap-1">
            <button
              class="h-8 w-8 flex items-center justify-center rounded text-sm text-zinc-400 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              disabled={page <= 1}
              onclick={() => goPage(page - 1)}
            >&lsaquo;</button>
            {#each pageNumbers as p}
              {#if p === "..."}
                <span class="h-8 w-8 flex items-center justify-center text-sm text-zinc-600">&hellip;</span>
              {:else}
                <button
                  class="h-8 w-8 flex items-center justify-center rounded text-sm transition-colors {p === page ? 'bg-white/10 text-white font-medium' : 'text-zinc-400 hover:text-white hover:bg-white/10'}"
                  onclick={() => goPage(p)}
                >{p}</button>
              {/if}
            {/each}
            <button
              class="h-8 w-8 flex items-center justify-center rounded text-sm text-zinc-400 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              disabled={page >= totalPages}
              onclick={() => goPage(page + 1)}
            >&rsaquo;</button>
          </div>
        </div>

        {#if error}
          <div class="px-3 py-2 rounded border border-rose-500/30 bg-rose-500/10 text-sm text-rose-200 mb-4">
            {error}
          </div>
        {/if}

        <!-- Table -->
        {#if initialLoad}
          <Skeleton lines={8} />
        {:else if viewers.length === 0}
          <EmptyState message={t("viewers.no_viewers")} />
        {:else}
          <!-- Mobile: card layout -->
          <div class="md:hidden space-y-2">
            {#each viewers as viewer (viewer.id)}
              <button
                class="w-full text-left p-4 rounded-lg border border-white/5 bg-white/[0.02] hover:bg-white/[0.04] transition-colors"
                onclick={() => openDrawer(viewer)}
              >
                <div class="flex items-center justify-between mb-2">
                  <span class="inline-flex items-center gap-2 text-white font-medium text-sm truncate mr-2">
                    <Icon icon="mdi:account-outline" class="w-4 h-4 text-zinc-600 shrink-0" />
                    {viewer.label || "-"}
                  </span>
                  <span class="inline-flex items-center gap-1.5 text-xs shrink-0 {viewer.status === 'active' ? 'text-green-500' : 'text-rose-500'}">
                    <Icon icon="mdi:circle" class="w-2.5 h-2.5" />
                    <span class="capitalize">{viewer.status}</span>
                  </span>
                </div>
                <div class="flex items-center gap-4 text-xs text-zinc-400">
                  <span class="inline-flex items-center gap-1">
                    {#if viewer.all_sites}
                      <Icon icon="mdi:check-circle" class="w-3.5 h-3.5 text-green-500" />
                      <span>{t("viewers.all_sites")}</span>
                    {:else}
                      <Icon icon="mdi:close-circle" class="w-3.5 h-3.5 text-zinc-600" />
                      <span>{t("viewers.limited")}</span>
                    {/if}
                  </span>
                  <span class="text-zinc-600">&middot;</span>
                  <span>{formatDate(viewer.created_at)}</span>
                </div>
              </button>
            {/each}
          </div>

          <!-- Desktop: table layout -->
          <div class="hidden md:block overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="border-b border-white/10">
                  <th class="text-left py-3 px-4 text-xs uppercase tracking-wide text-zinc-500 font-medium cursor-pointer hover:text-zinc-300" onclick={() => toggleSort("label")}>
                    {t("viewers.label")}
                    {#if sortField === "label"}
                      <Icon icon={sortOrder === "asc" ? "mdi:arrow-up" : "mdi:arrow-down"} class="w-3 h-3 inline" />
                    {/if}
                  </th>
                  <th class="text-center py-3 px-4 text-xs uppercase tracking-wide text-zinc-500 font-medium cursor-pointer hover:text-zinc-300" onclick={() => toggleSort("status")}>
                    {t("viewers.status")}
                    {#if sortField === "status"}
                      <Icon icon={sortOrder === "asc" ? "mdi:arrow-up" : "mdi:arrow-down"} class="w-3 h-3 inline" />
                    {/if}
                  </th>
                  <th class="text-center py-3 px-4 text-xs uppercase tracking-wide text-zinc-500 font-medium">{t("viewers.all_sites")}</th>
                  <th class="text-left py-3 px-4 text-xs uppercase tracking-wide text-zinc-500 font-medium cursor-pointer hover:text-zinc-300" onclick={() => toggleSort("created_at")}>
                    {t("viewers.created")}
                    {#if sortField === "created_at"}
                      <Icon icon={sortOrder === "asc" ? "mdi:arrow-up" : "mdi:arrow-down"} class="w-3 h-3 inline" />
                    {/if}
                  </th>
                </tr>
              </thead>
              <tbody>
                {#each viewers as viewer (viewer.id)}
                  <tr
                    class="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer transition-colors"
                    onclick={() => openDrawer(viewer)}
                  >
                    <td class="py-3 px-4 text-white font-medium"><span class="inline-flex items-center gap-2"><Icon icon="mdi:account-outline" class="w-4 h-4 text-zinc-600 shrink-0" />{viewer.label || "-"}</span></td>
                    <td class="py-3 px-4 text-center">
                      <Icon icon="mdi:circle" class="w-3 h-3 inline-block {viewer.status === 'active' ? 'text-green-500' : 'text-rose-500'}" />
                    </td>
                    <td class="py-3 px-4 text-center">
                      {#if viewer.all_sites}
                        <Icon icon="mdi:check-circle" class="w-4 h-4 inline-block text-green-500" />
                      {:else}
                        <Icon icon="mdi:close-circle" class="w-4 h-4 inline-block text-zinc-600" />
                      {/if}
                    </td>
                    <td class="py-3 px-4 text-zinc-500 whitespace-nowrap">{formatDate(viewer.created_at)}</td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
        {/if}
      </div>
    </div>
  </div>

  <!-- Detail drawer -->
  {#if drawerItem}
    <SlideOver onclose={closeDrawer} title={drawerItem.label || t("nav.viewers")} width="480px">
      <div class="flex items-center gap-2 text-sm text-zinc-400 mb-6">
        <Icon icon="mdi:circle" class="w-2.5 h-2.5 {drawerItem.status === 'active' ? 'text-green-500' : 'text-rose-500'}" />
        {drawerItem.status} &middot; {formatDate(drawerItem.created_at)}
      </div>

      <!-- Edit form -->
      <div class="space-y-4">
        <div>
          <label class="block text-xs text-zinc-300 mb-1.5">{t("viewers.label")}</label>
          <input type="text" class="w-full h-9 px-3 bg-black/20 border border-white/10 rounded text-sm text-white focus:outline-none focus:border-white/25" bind:value={editLabel} />
        </div>
        <div>
          <label class="block text-xs text-zinc-300 mb-1.5">{t("viewers.status")}</label>
          <select class="w-full h-9 px-3 bg-black/20 border border-white/10 rounded text-sm text-white focus:outline-none focus:border-white/25 cursor-pointer" bind:value={editStatus}>
            <option value="active" class="bg-zinc-900">{t("viewers.active")}</option>
            <option value="revoked" class="bg-zinc-900">{t("viewers.revoked")}</option>
          </select>
        </div>
        <label class="flex items-center gap-3 cursor-pointer py-1">
          <input type="checkbox" class="w-4 h-4 rounded border-white/20 bg-black/30" bind:checked={editAllSites} />
          <span class="text-sm text-zinc-200">{t("viewers.access_all")}</span>
        </label>

        <div class="flex gap-2 pt-2">
          <button class="ui-btn-accent flex-1 !py-2 !text-sm !gap-2 !rounded" onclick={handleSave} disabled={saving}>
            <Icon icon="mdi:content-save-outline" class="w-4 h-4" />
            {saving ? t("viewers.saving") : t("common.save")}
          </button>
          <button class="flex-1 inline-flex items-center justify-center gap-2 h-9 rounded bg-rose-500 text-white text-sm font-medium hover:bg-rose-400 transition-colors" onclick={() => deleteConfirm = true}>
            <Icon icon="mdi:trash-can-outline" class="w-4 h-4" />
            {t("common.delete")}
          </button>
        </div>
      </div>
    </SlideOver>
  {/if}

  <!-- Delete confirmation -->
  {#if deleteConfirm}
    <div class="fixed inset-0 z-[200] flex items-center justify-center">
      <button class="absolute inset-0 bg-black/60" onclick={() => (deleteConfirm = false)} aria-label="Cancel delete"></button>
      <div class="relative ui-card p-6 max-w-sm border border-rose-500/30 bg-rose-500/5">
        <h3 class="text-base font-semibold text-white mb-2">{t("viewers.confirm_delete")}</h3>
        <p class="text-sm text-zinc-400 mb-4">{t("sites.delete_warning")}</p>
        <div class="flex justify-end gap-2">
          <button class="ui-btn" onclick={() => (deleteConfirm = false)}>{t("common.cancel")}</button>
          <button
            class="ui-btn bg-rose-600 border-rose-500 text-white hover:bg-rose-500"
            onclick={confirmDelete}
            disabled={deleting}
          >
            {deleting ? t("viewers.deleting") : t("common.delete")}
          </button>
        </div>
      </div>
    </div>
  {/if}
</Layout>
